import { graphql, useMutation } from "react-relay"
import { useConsultEmergencyServiceMutation as useConsultEmergencyServiceMutationType } from "@/modules/patients/components/header/emergency-service/services/ch/__generated__/useConsultEmergencyServiceMutation.graphql"
import { useGenesysMutationErrorHandling } from "@/modules/telephony/services/useGenesysMutationErrorHandling"
import { Country } from "@/types/__generated__/union-types"

const consultEmergencyServiceMutation = graphql`
  mutation useConsultEmergencyServiceMutation($input: ConsultEmergencyServiceInput!) {
    consultEmergencyService(input: $input) {
      errors {
        ... on AgentNotAuthenticated {
          __typename
        }
        ... on InvalidEmergencyPhoneNumber {
          __typename
        }
        ... on InvalidPatientPhoneNumber {
          __typename
        }
        ... on NoActiveCallFound {
          __typename
        }
        ... on SeveralActiveCallsFound {
          __typename
        }
        ... on PatientNotOnCall {
          __typename
        }
        ... on ConsultAlreadyInProgress {
          __typename
        }
        ... on ConsultRejected {
          __typename
        }
      }
    }
  }
`

const consultEmergencyServiceErrorMessageKeyByTypename = {
  InvalidEmergencyPhoneNumber: "telephony.consultActions.error.InvalidEmergencyPhoneNumber",
  InvalidPatientPhoneNumber: "telephony.consultActions.error.InvalidPatientPhoneNumber",
  NoActiveCallFound: "telephony.consultActions.error.NoActiveCallFound",
  SeveralActiveCallsFound: "telephony.consultActions.error.SeveralActiveCallsFound",
  PatientNotOnCall: "telephony.consultActions.error.PatientNotOnCall",
  ConsultAlreadyInProgress: "telephony.consultActions.error.ConsultAlreadyInProgress",
  ConsultRejected: "telephony.consultActions.error.ConsultRejected",
}

const useConsultEmergencyServiceMutation = () => {
  const [commit, isInFlight] = useMutation<useConsultEmergencyServiceMutationType>(consultEmergencyServiceMutation)
  const commitWithGenesysErrorHandling = useGenesysMutationErrorHandling(
    commit,
    (response) => response.consultEmergencyService.errors,
    consultEmergencyServiceErrorMessageKeyByTypename,
  )

  const consultEmergencyService = (
    emergencyPhoneNumber: string,
    patientId: string | undefined,
    phoneNumbers: ReadonlyArray<string>,
    country: Country,
    onAgentNotAuthenticated: VoidFunction,
  ) => {
    commitWithGenesysErrorHandling({
      variables: { input: { emergencyPhoneNumber, patientId, phoneNumbers, country } },
      onAgentNotAuthenticated,
    })
  }

  return [consultEmergencyService, isInFlight] as const
}

export { useConsultEmergencyServiceMutation }
